import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { SignupState, SignupStep, SignupData, PaymentDetails, FlexiblePricingConfig } from '../../types';
import { calculatePricing, DEFAULT_PRICING } from '../../services/stripe';

// Action types
type SignupAction =
  | { type: 'SET_STEP'; payload: SignupStep }
  | { type: 'UPDATE_RESTAURANT_DATA'; payload: Partial<SignupData['restaurant']> }
  | { type: 'UPDATE_MANAGER_DATA'; payload: Partial<SignupData['manager']> }
  | { type: 'UPDATE_TABLE_COUNT'; payload: number }
  | { type: 'UPDATE_NFC_COUNT'; payload: number }
  | { type: 'UPDATE_TABLET_COUNT'; payload: number }
  | { type: 'UPDATE_PRICING'; payload: FlexiblePricingConfig }
  | { type: 'SET_PAYMENT_DETAILS'; payload: PaymentDetails }
  | { type: 'SET_ERROR'; payload: { field: string; message: string } }
  | { type: 'CLEAR_ERROR'; payload: string }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'RESET_SIGNUP' }
  | { type: 'SET_NEGOTIATED_PRICING'; payload: boolean }
  | { type: 'SET_CUSTOM_SALES_TOKEN'; payload: string }
  | { type: 'SET_ADDITIONAL_RESTAURANT_MODE'; payload: SignupData['manager'] };

// Initial state
const initialState: SignupState = {
  currentStep: 'restaurant',
  data: {
    restaurant: {
      name: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      phone: '',
      email: '',
      website: '',
      description: '',
      status: 'pending',
    },
    manager: {
      displayName: '',
      email: '',
      phoneNumber: '',
      gmailAddress: '',
      role: 'managerAdmin',
    },
    tableCount: 1,
    nfcCount: 1,
    tabletCount: 1,
    pricing: DEFAULT_PRICING,
  },
  paymentDetails: null,
  errors: {},
  isLoading: false,
  isNegotiatedPricing: false,
  // Additional restaurant flow state
  isAdditionalRestaurant: false,
  storedManagerData: undefined,
};

// Reducer
const signupReducer = (state: SignupState, action: SignupAction): SignupState => {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, currentStep: action.payload };

    case 'UPDATE_RESTAURANT_DATA':
      return {
        ...state,
        data: {
          ...state.data,
          restaurant: { ...state.data.restaurant, ...action.payload } as SignupData['restaurant'],
        },
      };

    case 'UPDATE_MANAGER_DATA':
      return {
        ...state,
        data: {
          ...state.data,
          manager: { ...state.data.manager, ...action.payload } as SignupData['manager'],
        },
      };

    case 'UPDATE_TABLE_COUNT':
      const newTableCount = action.payload;
      const updatedDataWithTables = {
        ...state.data,
        tableCount: newTableCount,
      };

      // Recalculate pricing when table count changes
      if (updatedDataWithTables.tableCount !== undefined && updatedDataWithTables.nfcCount !== undefined && updatedDataWithTables.pricing) {
        const calculation = calculatePricing(
          updatedDataWithTables.tableCount,
          updatedDataWithTables.nfcCount,
          updatedDataWithTables.tabletCount || 0,
          updatedDataWithTables.pricing
        );
        
        return {
          ...state,
          data: updatedDataWithTables,
          paymentDetails: {
            ...state.paymentDetails,
            calculation,
            status: 'pending',
          },
        };
      }

      return { ...state, data: updatedDataWithTables };

    case 'UPDATE_NFC_COUNT':
      const newNfcCount = action.payload;
      const updatedDataWithNfc = {
        ...state.data,
        nfcCount: newNfcCount,
      };

      // Recalculate pricing when NFC count changes
      if (updatedDataWithNfc.tableCount !== undefined && updatedDataWithNfc.nfcCount !== undefined && updatedDataWithNfc.pricing) {
        const calculation = calculatePricing(
          updatedDataWithNfc.tableCount,
          updatedDataWithNfc.nfcCount,
          updatedDataWithNfc.tabletCount || 0,
          updatedDataWithNfc.pricing
        );
        
        return {
          ...state,
          data: updatedDataWithNfc,
          paymentDetails: {
            ...state.paymentDetails,
            calculation,
            status: 'pending',
          },
        };
      }

      return { ...state, data: updatedDataWithNfc };

    case 'UPDATE_TABLET_COUNT':
      const newTabletCount = action.payload;
      const updatedDataWithTablets = {
        ...state.data,
        tabletCount: newTabletCount,
      };

      // Recalculate pricing when tablet count changes
      if (updatedDataWithTablets.tableCount !== undefined && updatedDataWithTablets.nfcCount !== undefined && updatedDataWithTablets.pricing) {
        const calculation = calculatePricing(
          updatedDataWithTablets.tableCount,
          updatedDataWithTablets.nfcCount,
          updatedDataWithTablets.tabletCount || 0,
          updatedDataWithTablets.pricing
        );
        
        return {
          ...state,
          data: updatedDataWithTablets,
          paymentDetails: {
            ...state.paymentDetails,
            calculation,
            status: 'pending',
          },
        };
      }

      return { ...state, data: updatedDataWithTablets };

    case 'UPDATE_PRICING':
      const updatedDataWithPricing = {
        ...state.data,
        pricing: action.payload,
      };

      // Recalculate pricing when pricing config changes
      if (updatedDataWithPricing.tableCount !== undefined && updatedDataWithPricing.nfcCount !== undefined) {
        const calculation = calculatePricing(
          updatedDataWithPricing.tableCount,
          updatedDataWithPricing.nfcCount,
          updatedDataWithPricing.tabletCount || 0,
          updatedDataWithPricing.pricing
        );
        
        return {
          ...state,
          data: updatedDataWithPricing,
          paymentDetails: {
            ...state.paymentDetails,
            calculation,
            status: 'pending',
          },
        };
      }

      return { ...state, data: updatedDataWithPricing };

    case 'SET_PAYMENT_DETAILS':
      return { ...state, paymentDetails: action.payload };

    case 'SET_ERROR':
      return {
        ...state,
        errors: { ...state.errors, [action.payload.field]: action.payload.message },
      };

    case 'CLEAR_ERROR':
      const { [action.payload]: _, ...remainingErrors } = state.errors;
      return { ...state, errors: remainingErrors };

    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };

    case 'SET_NEGOTIATED_PRICING':
      return { ...state, isNegotiatedPricing: action.payload };

    case 'SET_CUSTOM_SALES_TOKEN':
      return {
        ...state,
        data: {
          ...state.data,
          customSalesToken: action.payload,
        },
      };

    case 'SET_ADDITIONAL_RESTAURANT_MODE':
      return {
        ...state,
        isAdditionalRestaurant: true,
        storedManagerData: action.payload,
        currentStep: 'restaurant',
        // Reset restaurant data but keep manager data stored
        data: {
          ...state.data,
          restaurant: {
            name: '',
            address: '',
            city: '',
            state: '',
            zipCode: '',
            phone: '',
            email: '',
            website: '',
            description: '',
            status: 'pending',
          },
          manager: action.payload, // Use stored manager data
          tableCount: 1,
          nfcCount: 1,
          tabletCount: 1,
          customSalesToken: '', // Reset custom sales token for new restaurant
        },
        errors: {},
        isLoading: false,
        isNegotiatedPricing: false,
      };

    case 'RESET_SIGNUP':
      return initialState;

    default:
      return state;
  }
};

// Context
interface SignupContextType {
  state: SignupState;
  dispatch: React.Dispatch<SignupAction>;
  // Helper functions
  nextStep: () => void;
  previousStep: () => void;
  goToStep: (step: SignupStep) => void;
  updateRestaurantData: (data: Partial<SignupData['restaurant']>) => void;
  updateManagerData: (data: Partial<SignupData['manager']>) => void;
  updateTableCount: (count: number) => void;
  updateNfcCount: (count: number) => void;
  updateTabletCount: (count: number) => void;
  updatePricing: (pricing: FlexiblePricingConfig) => void;
  setError: (field: string, message: string) => void;
  clearError: (field: string) => void;
  setLoading: (loading: boolean) => void;
  resetSignup: () => void;
  setNegotiatedPricing: (isNegotiated: boolean) => void;
  setCustomSalesToken: (token: string) => void;
  startAdditionalRestaurant: (managerData: SignupData['manager']) => void;
}

const SignupContext = createContext<SignupContextType | undefined>(undefined);

// Step order
const STEP_ORDER: SignupStep[] = ['restaurant', 'manager', 'tables', 'pricing', 'payment', 'processing', 'success'];

// Provider
interface SignupProviderProps {
  children: ReactNode;
}

export const SignupProvider: React.FC<SignupProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(signupReducer, initialState);

  const nextStep = () => {
    const currentIndex = STEP_ORDER.indexOf(state.currentStep);
    if (currentIndex < STEP_ORDER.length - 1) {
      let nextStepIndex = currentIndex + 1;
      
      // Skip manager step if in additional restaurant mode
      if (state.isAdditionalRestaurant && STEP_ORDER[nextStepIndex] === 'manager') {
        nextStepIndex += 1;
      }
      
      if (nextStepIndex < STEP_ORDER.length) {
        dispatch({ type: 'SET_STEP', payload: STEP_ORDER[nextStepIndex] });
        // Scroll to top when navigating to next step
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const previousStep = () => {
    const currentIndex = STEP_ORDER.indexOf(state.currentStep);
    if (currentIndex > 0) {
      let prevStepIndex = currentIndex - 1;
      
      // Skip manager step if in additional restaurant mode
      if (state.isAdditionalRestaurant && STEP_ORDER[prevStepIndex] === 'manager') {
        prevStepIndex -= 1;
      }
      
      if (prevStepIndex >= 0) {
        dispatch({ type: 'SET_STEP', payload: STEP_ORDER[prevStepIndex] });
        // Scroll to top when navigating to previous step
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const goToStep = (step: SignupStep) => {
    dispatch({ type: 'SET_STEP', payload: step });
    // Scroll to top when navigating to any step
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const updateRestaurantData = (data: Partial<SignupData['restaurant']>) => {
    dispatch({ type: 'UPDATE_RESTAURANT_DATA', payload: data });
  };

  const updateManagerData = (data: Partial<SignupData['manager']>) => {
    dispatch({ type: 'UPDATE_MANAGER_DATA', payload: data });
  };

  const updateTableCount = (count: number) => {
    dispatch({ type: 'UPDATE_TABLE_COUNT', payload: count });
  };

  const updateNfcCount = (count: number) => {
    dispatch({ type: 'UPDATE_NFC_COUNT', payload: count });
  };

  const updateTabletCount = (count: number) => {
    dispatch({ type: 'UPDATE_TABLET_COUNT', payload: count });
  };

  const updatePricing = (pricing: FlexiblePricingConfig) => {
    dispatch({ type: 'UPDATE_PRICING', payload: pricing });
  };

  const setError = (field: string, message: string) => {
    dispatch({ type: 'SET_ERROR', payload: { field, message } });
  };

  const clearError = (field: string) => {
    dispatch({ type: 'CLEAR_ERROR', payload: field });
  };

  const setLoading = (loading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: loading });
  };

  const resetSignup = () => {
    dispatch({ type: 'RESET_SIGNUP' });
  };

  const setNegotiatedPricing = (isNegotiated: boolean) => {
    dispatch({ type: 'SET_NEGOTIATED_PRICING', payload: isNegotiated });
  };

  const setCustomSalesToken = (token: string) => {
    dispatch({ type: 'SET_CUSTOM_SALES_TOKEN', payload: token });
  };

  const startAdditionalRestaurant = (managerData: SignupData['manager']) => {
    dispatch({ type: 'SET_ADDITIONAL_RESTAURANT_MODE', payload: managerData });
  };

  const value: SignupContextType = {
    state,
    dispatch,
    nextStep,
    previousStep,
    goToStep,
    updateRestaurantData,
    updateManagerData,
    updateTableCount,
    updateNfcCount,
    updateTabletCount,
    updatePricing,
    setError,
    clearError,
    setLoading,
    resetSignup,
    setNegotiatedPricing,
    setCustomSalesToken,
    startAdditionalRestaurant,
  };

  return (
    <SignupContext.Provider value={value}>
      {children}
    </SignupContext.Provider>
  );
};

// Hook
export const useSignup = () => {
  const context = useContext(SignupContext);
  if (context === undefined) {
    throw new Error('useSignup must be used within a SignupProvider');
  }
  return context;
};

export default SignupProvider;
